import type { Link, PhrasingContent, Root } from 'mdast';
import remarkParse from 'remark-parse';
import { unified } from 'unified';
import { visit } from 'unist-util-visit';
import { AI_CHAT_CUSTOM_PROTOCOL_PREFIX as PORTAL_TARGET, resolveAiChatLink } from './aiChatLinkResolver';

// `[O002] Orion Group](crespect://contact/12)`: the label's own opening bracket is missing, so the whole
// link is text. A half-escaped pair (`[\[O002] …`) reads the same with one more `[` in front, which is
// taken along.
const UNOPENED_LINK = new RegExp(String.raw`\[?(\[[^[\]\n]+\] [^[\]\n]+)\]\((${PORTAL_TARGET}[^\s()]+)\)`, 'gi');
// `[[O002\] Orion Group](…)`: the number's opening bracket fell outside the link, leaving this label.
const LABEL_MISSING_ITS_OPENING_BRACKET = /^[^[\]]+\] \S/;
// `[[Orion Group]](…)`: brackets doubled around a whole name.
const LABEL_IN_BRACKETS = /^\[([^[\]]+)\]$/;
const CONTACT_TARGET = `${PORTAL_TARGET}contact/`;
const spanParser = unified().use(remarkParse);

// The link a code span spells out when it holds one portal link and nothing else; a target the chat
// cannot open keeps the span as code, so the address stays readable. An autolink, `<crespect://…>`,
// has no label and stays code too.
const portalLinkSpelledIn = (code: string): Link | undefined => {
    // Every inline link holds `](`; most spans do not, and skip the parse.
    if (!code.includes('](')) {
        return undefined;
    }
    const [block, ...otherBlocks] = spanParser.parse(code).children;
    if (
        code.trimStart().startsWith('<') ||
        otherBlocks.length > 0 ||
        block?.type !== 'paragraph' ||
        block.children.length !== 1
    ) {
        return undefined;
    }
    const [link] = block.children;
    return link.type === 'link' && resolveAiChatLink(link.url).kind === 'internal' ? link : undefined;
};

const linksIn = (text: string): PhrasingContent[] => {
    const nodes: PhrasingContent[] = [];
    let end = 0;

    for (const match of text.matchAll(UNOPENED_LINK)) {
        const link: Link = { type: 'link', url: match[2], title: null, children: [{ type: 'text', value: match[1] }] };
        nodes.push({ type: 'text', value: text.slice(end, match.index) }, link);
        end = match.index + match[0].length;
    }

    return end === 0 ? [] : [...nodes, { type: 'text', value: text.slice(end) }];
};

/**
 * Puts back the portal links a model wrote in backticks or with a bracket missing. A code span holding
 * one portal link and nothing else — `` `[Timecard #12](crespect://timecards/12)` `` — becomes that link,
 * first, so the label repair below reads it like any other. A client's label opens with its number in
 * brackets — `[[O002] Orion Group](crespect://contact/12)` — which is valid Markdown the model does not
 * always write whole: the shapes above reach the chat as raw text or as a link with a stray bracket.
 *
 * It works on the parsed tree, so no fenced block is read and a code span's text is only parsed for that
 * lone link, never repaired, and it keeps no state, so a message is repaired the same way at every streamed
 * chunk. Only `crespect://` targets are touched, and the label of a link already parsed only on a contact.
 */
export const repairAiChatLinkLabels = () => (tree: Root) => {
    visit(tree, 'inlineCode', (node, index, parent) => {
        if (!parent || index === undefined || parent.type === 'link') {
            return;
        }
        const link = portalLinkSpelledIn(node.value);
        if (link) {
            (parent.children as PhrasingContent[])[index] = link;
        }
    });

    visit(tree, 'text', (node, index, parent) => {
        if (!parent || index === undefined || parent.type === 'link') {
            return undefined;
        }
        const nodes = linksIn(node.value);
        if (nodes.length === 0) {
            return undefined;
        }
        (parent.children as PhrasingContent[]).splice(index, 1, ...nodes);
        return index + nodes.length;
    });

    visit(tree, 'link', (node, index, parent) => {
        const [label] = node.children;
        // Both shapes are valid links: only a contact's label opens with a number, elsewhere a bracket is the name's own.
        if (!node.url.toLowerCase().startsWith(CONTACT_TARGET) || node.children.length !== 1 || label.type !== 'text') {
            return;
        }
        const bracketed = LABEL_IN_BRACKETS.exec(label.value);
        if (bracketed) {
            label.value = bracketed[1];
            return;
        }
        if (!LABEL_MISSING_ITS_OPENING_BRACKET.test(label.value)) {
            return;
        }
        label.value = `[${label.value}`;
        // The bracket that should have opened the number sits just before the link, as text.
        const before = parent && index !== undefined && index > 0 ? parent.children[index - 1] : undefined;
        if (before?.type === 'text' && before.value.endsWith('[')) {
            before.value = before.value.slice(0, -1);
        }
    });
};
